import * as React from 'react'
import { CalendarIcon } from '@/src/icons'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'

import { cn } from '@/registry/reapit/lib/utils'
import { Button } from '@/registry/reapit/ui/button'
import { Calendar } from '@/registry/reapit/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/registry/reapit/ui/popover'

// ---------------------------------------------------------------------------
// DatePicker
// ---------------------------------------------------------------------------

export namespace DatePicker {
  export interface Props {
    /** The controlled selected date. */
    value?: Date
    /** The initial date when uncontrolled. */
    defaultValue?: Date
    /** Called when the selected date changes. */
    onValueChange?: (date: Date | undefined) => void
    /** Placeholder text shown when no date is selected. */
    placeholder?: string
    /** Whether the date picker is disabled. */
    disabled?: boolean
    /** Additional class name applied to the trigger button. */
    className?: string
    /** Format string passed to date-fns `format`. Defaults to "PPP". */
    dateFormat?: string
  }
}

export function DatePicker({
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  dateFormat = 'PPP',
}: DatePicker.Props) {
  const [open, setOpen] = React.useState(false)
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(defaultValue)

  const isControlled = value !== undefined
  const selected = isControlled ? value : internalDate

  function handleSelect(date: Date | undefined) {
    if (!isControlled) {
      setInternalDate(date)
    }
    onValueChange?.(date)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-slot="date-picker"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-[240px] justify-start text-left font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          {/* DS: picker button icon is icon-md (size-5 = 20 px). */}
          <CalendarIcon className="size-5" />
          {selected ? format(selected, dateFormat) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} onSelect={handleSelect} autoFocus />
      </PopoverContent>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// DateRangePicker
// ---------------------------------------------------------------------------

export namespace DateRangePicker {
  export interface Props {
    /** The controlled selected date range. */
    value?: DateRange
    /** The initial date range when uncontrolled. */
    defaultValue?: DateRange
    /** Called when the selected date range changes. */
    onValueChange?: (range: DateRange | undefined) => void
    /** Placeholder text shown when no range is selected. */
    placeholder?: string
    /** Whether the date range picker is disabled. */
    disabled?: boolean
    /** Additional class name applied to the trigger button. */
    className?: string
    /** Format string passed to date-fns `format`. Defaults to "PPP". */
    dateFormat?: string
    /** Number of months to display. Defaults to 2. */
    numberOfMonths?: number
  }
}

export function DateRangePicker({
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Pick a date range',
  disabled = false,
  className,
  dateFormat = 'LLL dd, y',
  numberOfMonths = 2,
}: DateRangePicker.Props) {
  const [open, setOpen] = React.useState(false)
  const [internalRange, setInternalRange] = React.useState<DateRange | undefined>(defaultValue)

  const isControlled = value !== undefined
  const selected = isControlled ? value : internalRange

  function handleSelect(range: DateRange | undefined) {
    if (!isControlled) {
      setInternalRange(range)
    }
    onValueChange?.(range)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-slot="date-range-picker"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-[300px] justify-start text-left font-normal',
            !selected?.from && 'text-muted-foreground',
            className,
          )}
        >
          {/* DS: picker button icon is icon-md (size-5 = 20 px). */}
          <CalendarIcon className="size-5" />
          {selected?.from ? (
            selected.to ? (
              <>
                {format(selected.from, dateFormat)} – {format(selected.to, dateFormat)}
              </>
            ) : (
              format(selected.from, dateFormat)
            )
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="range" selected={selected} onSelect={handleSelect} numberOfMonths={numberOfMonths} autoFocus />
      </PopoverContent>
    </Popover>
  )
}
